<script setup lang="ts">
/**
 * AuthenticatedLayout
 * Centralized shell for authenticated routes
 * Renders mobile shell (MobileHeader + BottomTabBar) on phones
 * Renders desktop shell (AppHeader + AppNavigation) on larger screens
 */

import { computed, provide, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores'
import { useIsMobile } from '@/composables/useIsMobile'
import { AppHeader, AppNavigation, ContactModal } from '@/components/common'
import { BottomTabBar, MobileHeader, QuickUploadSheet } from '@/components/mobile'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { isMobile } = useIsMobile()
const { t, te } = useI18n()

// Current page title (localized per route name)
const pageTitle = computed(() => {
  const name = route.name as string
  const key = `layout.pageTitle.${name}`
  return te(key) ? t(key) : 'P&G Health'
})

// Whether to show back button (for drill-in pages)
const showBackButton = computed(() => {
  // Show back for pages that aren't main tabs
  const mainTabs = ['Dashboard', 'OwnerDashboard', 'Challenges', 'TicketStatus', 'CashBalance', 'Profile', 'Notifications']
  return !mainTabs.includes(route.name as string)
})

const mobileHeaderLeftAction = computed(() => {
  if (route.name === 'Profile') {
    return {
      icon: 'pi pi-sign-out',
      ariaLabel: t('layout.logout'),
    }
  }
  return null
})

// User role for tab bar
const userRole = computed(() => authStore.user?.role || 'pharmacist')

// Toast for success messages
const toast = useToast()

// Quick action sheets state
const showUploadSheet = ref(false)
const isLoggingOut = ref(false)
const showContactModal = ref(false)

// Provide isMobile to child components
provide('isMobile', isMobile)

// FAB state - can be controlled by views
const showFab = ref(false)
const fabIcon = ref('pi pi-plus')
const fabAction = ref<(() => void) | null>(null)

// Provide FAB control to child views
provide('fabControl', {
  show: (icon: string, action: () => void) => {
    showFab.value = true
    fabIcon.value = icon
    fabAction.value = action
  },
  hide: () => {
    showFab.value = false
    fabAction.value = null
  },
})

// Handlers
async function handleLogout() {
  if (isLoggingOut.value) return
  isLoggingOut.value = true

  try {
    await authStore.logout()
  } finally {
    isLoggingOut.value = false
    await router.replace({ name: 'Login' })
  }
}

function handleMenuToggle() {
  // Mobile menu toggle - handled by AppHeader
}

function handleFabClick() {
  if (fabAction.value) {
    fabAction.value()
  }
}

// Handle center action button from BottomTabBar
function handleCenterAction() {
  showUploadSheet.value = true
}

// Handle upload success
function handleUploadSuccess(count: number) {
  toast.add({
    severity: 'success',
    summary: t('layout.ticketsUploaded'),
    detail: t('layout.ticketsUploadedDetail', { count }, count),
    life: 3000
  })
}

</script>

<template>
  <div class="authenticated-layout" :class="{ 'is-mobile': isMobile }">
    <!-- Desktop Shell -->
    <template v-if="!isMobile">
      <AppHeader
        :user="authStore.user"
        @logout="handleLogout"
        @menu-toggle="handleMenuToggle"
        @open-contact="showContactModal = true"
      />
      <AppNavigation
        v-if="authStore.user"
        :user-role="authStore.user.role"
      />
    </template>

    <!-- Mobile Shell -->
    <template v-else>
      <MobileHeader
        :title="pageTitle"
        :show-back="showBackButton"
        :left-icon="mobileHeaderLeftAction?.icon"
        :left-aria-label="mobileHeaderLeftAction?.ariaLabel"
        @left-action="handleLogout"
        @contact-action="showContactModal = true"
      />
    </template>

    <!-- Main Content Area -->
    <main
      class="layout-content"
      :class="{
        'mobile-content': isMobile,
        'desktop-content': !isMobile
      }"
    >
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Mobile Bottom Tab Bar -->
    <BottomTabBar
      v-if="isMobile && authStore.user"
      :user-role="userRole"
      @action="handleCenterAction"
    />

    <!-- Quick Upload Sheet (Pharmacists) -->
    <QuickUploadSheet
      v-if="isMobile"
      v-model:visible="showUploadSheet"
      @success="handleUploadSuccess"
    />

    <!-- Contact Modal -->
    <ContactModal v-model:visible="showContactModal" />

    <!-- Mobile FAB (controlled by views) -->
    <button
      v-if="isMobile && showFab"
      type="button"
      class="mobile-fab tap-feedback"
      :aria-label="t('layout.mainAction')"
      @click="handleFabClick"
    >
      <i :class="fabIcon" aria-hidden="true"></i>
    </button>
  </div>
</template>

<style scoped>
.authenticated-layout {
  min-height: 100vh;
  min-height: 100dvh; /* Dynamic viewport height - doesn't change on scroll */
  background: var(--surface-ground);
}

/* Mobile layout fix for address bar show/hide */
.authenticated-layout.is-mobile {
  /* Use fixed height based on dynamic viewport */
  min-height: -webkit-fill-available;
  min-height: 100dvh;
  overflow-x: hidden;
}

/* Desktop content */
.desktop-content {
  max-width: 80rem;
  margin: 0 auto;
  padding: 1.5rem 1rem;
}

@media (min-width: 640px) {
  .desktop-content {
    padding: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .desktop-content {
    padding: 2rem;
  }
}

/* Mobile content */
.mobile-content {
  padding: 1rem;
  padding-top: calc(var(--mobile-header-height) + var(--safe-area-top) + 1rem);
  padding-bottom: calc(var(--mobile-tab-bar-height) + var(--safe-area-bottom) + 1rem);
  min-height: 100dvh;
}

/* Mobile FAB */
.mobile-fab {
  position: fixed;
  bottom: calc(var(--mobile-tab-bar-height) + var(--safe-area-bottom) + 16px);
  right: 16px;
  width: var(--mobile-fab-size);
  height: var(--mobile-fab-size);
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-pg-blue) 0%, var(--color-pg-blue-light) 100%);
  color: white;
  border: none;
  box-shadow: 0 8px 25px rgba(0, 61, 165, 0.35);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 50;
  transition: box-shadow var(--duration-normal) var(--ease-out);

  /* Prevent jumping on mobile scroll */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  contain: layout;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.mobile-fab:hover {
  transform: translateZ(0) scale(1.1);
  box-shadow: 0 12px 35px rgba(0, 61, 165, 0.45);
}

.mobile-fab:active {
  transform: translateZ(0) scale(0.95);
}

/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Tap feedback */
.tap-feedback:active {
  transform: scale(0.95);
}
</style>
